import React, { useRef, useEffect, useState } from 'react';
import './ElectricTimeline.css';

interface TimelineCard {
  id: string;
  title: string;
  description?: string;
  t: number; // position along curve (0-1)
  offsetY: number; // vertical offset from curve
}

interface ElectricTimelineProps {
  cards: TimelineCard[];
  className?: string;
}

const ElectricTimeline: React.FC<ElectricTimelineProps> = ({ cards, className = '' }) => {
  const wrapRef = useRef<HTMLDivElement>(null);
  const svgRef = useRef<SVGSVGElement>(null);
  const refPathRef = useRef<SVGPathElement>(null);
  const basePathRef = useRef<SVGPathElement>(null);
  const elecPathRef = useRef<SVGPathElement>(null);
  const sparksGroupRef = useRef<SVGGElement>(null);
  const animationRef = useRef<number | null>(null);

  const [dimensions, setDimensions] = useState({ width: 0, height: 0, containerStart: 0, containerEnd: 0 });
  const cardsState = useRef<Map<string, any>>(new Map());
  const sparksRef = useRef<any[]>([]);

  // Constants
  const MAG_RADIUS = 220;
  const MAG_STRENGTH = 0.18;
  const RETURN_SPRING = 0.08;
  const FRICTION = 0.86;
  const MAX_OFFSET = 90;
  const CURVE_RESP = 0.55;
  const CTRL_CAP = 140;
  const SPARKS_COUNT = 14;

  const clamp = (v: number, a: number, b: number) => Math.min(b, Math.max(a, v));

  // Helper function to find t value for a given X position on the curve
  const findTForX = (path: SVGPathElement, targetX: number, totalLength: number): number => {
    const segments = 100;
    let closestT = 0;
    let minDistance = Infinity;

    for (let i = 0; i <= segments; i++) {
      const t = i / segments;
      const point = path.getPointAtLength(t * totalLength);
      const distance = Math.abs(point.x - targetX);
      if (distance < minDistance) {
        minDistance = distance;
        closestT = t;
      }
    }
    return closestT;
  };

  const createSparks = () => {
    if (!sparksGroupRef.current) return;

    sparksRef.current = [];
    sparksGroupRef.current.innerHTML = '';

    for (let i = 0; i < SPARKS_COUNT; i++) {
      const circle = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
      circle.setAttribute('class', 'spark');
      circle.setAttribute('r', (2 + Math.random() * 2.4).toString());
      sparksGroupRef.current.appendChild(circle);

      sparksRef.current.push({
        el: circle,
        pos: Math.random(),
        speed: 0.08 + Math.random() * 0.25
      });
    }
  };

  const randomDash = (total: number): string => {
    const parts: number[] = [];
    let sum = 0;
    while (sum < total) {
      const on = 6 + Math.random() * 18;
      const off = 6 + Math.random() * 20;
      parts.push(on, off);
      sum += on + off;
    }
    return parts.join(' ');
  };

      const resize = () => {
    if (!wrapRef.current || !svgRef.current) return;

    const rect = wrapRef.current.getBoundingClientRect();
    const w = rect.width;
    const h = rect.height;

        // Calculate container bounds to match other sections
    const maxWidth = 1200;
    const containerPadding = 24;
    const availableWidth = w - (containerPadding * 2);
    const containerWidth = Math.min(maxWidth, availableWidth);
    const containerStart = (w - containerWidth) / 2;
    const containerEnd = containerStart + containerWidth;

    setDimensions({ width: w, height: h, containerStart, containerEnd });
    svgRef.current.setAttribute('viewBox', `0 0 ${w} ${h}`);

    // Line goes full width, but cards are positioned within container bounds
    const p0 = { x: -50, y: h * 0.78 };
    const p1 = { x: w * 0.28, y: h * 0.70 };
    const p2 = { x: w * 0.64, y: h * 0.56 };
    const p3 = { x: w + 50, y: h * 0.30 };

    const d = `M${p0.x},${p0.y} C${p1.x},${p1.y} ${p2.x},${p2.y} ${p3.x},${p3.y}`;

    if (refPathRef.current) refPathRef.current.setAttribute('d', d);
    if (basePathRef.current) basePathRef.current.setAttribute('d', d);
    if (elecPathRef.current) elecPathRef.current.setAttribute('d', d);
  };

  const computePath = (now: number) => {
    if (!refPathRef.current || !basePathRef.current || !elecPathRef.current) return;

    const dRef = refPathRef.current.getAttribute('d');
    if (!dRef) return;

    const nums = dRef.match(/-?\d+(\.\d+)?/g)?.map(Number);
    if (!nums || nums.length < 8) return;

    const p0 = { x: nums[0], y: nums[1] };
    const c1 = { x: nums[2], y: nums[3] };
    const c2 = { x: nums[4], y: nums[5] };
    const p3 = { x: nums[6], y: nums[7] };

    let dC1 = { x: 0, y: 0 };
    let dC2 = { x: 0, y: 0 };

    const Lref = refPathRef.current.getTotalLength();

    // Update card dynamics
    cards.forEach(card => {
      const state = cardsState.current.get(card.id);
      if (!state) return;

      const anchor = refPathRef.current!.getPointAtLength(clamp(card.t, 0, 1) * Lref);
      const targetX = anchor.x;
      const targetY = anchor.y + card.offsetY;

      let ax = 0, ay = 0;

      if (state.hover) {
        const x = targetX + state.dx;
        const y = targetY + state.dy;
        const dx = state.hx - x;
        const dy = state.hy - y;
        const dist = Math.hypot(dx, dy);

        if (dist < MAG_RADIUS) {
          const f = 1 - dist / MAG_RADIUS;
          ax += dx * MAG_STRENGTH * f;
          ay += dy * MAG_STRENGTH * f;
        }
      }

      ax += (targetX - (targetX + state.dx)) * RETURN_SPRING;
      ay += (targetY - (targetY + state.dy)) * RETURN_SPRING;

      state.vx = (state.vx + ax) * FRICTION;
      state.vy = (state.vy + ay) * FRICTION;
      state.dx += state.vx;
      state.dy += state.vy;

      const mag = Math.hypot(state.dx, state.dy);
      if (mag > MAX_OFFSET) {
        const k = MAX_OFFSET / mag;
        state.dx *= k;
        state.dy *= k;
      }

      const sigma = 0.22;
      const w1 = Math.exp(-Math.pow(card.t - 0.30, 2) / (2 * sigma * sigma));
      const w2 = Math.exp(-Math.pow(card.t - 0.70, 2) / (2 * sigma * sigma));

      dC1.x += state.dx * w1;
      dC1.y += state.dy * w1;
      dC2.x += state.dx * w2;
      dC2.y += state.dy * w2;

      // Position card element using the full curve length
      if (state.element) {
        const curvePoint = refPathRef.current!.getPointAtLength(clamp(card.t, 0, 1) * Lref);

        const x = curvePoint.x + state.dx;
        const y = curvePoint.y + card.offsetY + state.dy;

        (state.element as HTMLElement).style.left = `${x}px`;
        (state.element as HTMLElement).style.top = `${y}px`;
      }
    });

    dC1.x = clamp(dC1.x * CURVE_RESP, -CTRL_CAP, CTRL_CAP);
    dC1.y = clamp(dC1.y * CURVE_RESP, -CTRL_CAP, CTRL_CAP);
    dC2.x = clamp(dC2.x * CURVE_RESP, -CTRL_CAP, CTRL_CAP);
    dC2.y = clamp(dC2.y * CURVE_RESP, -CTRL_CAP, CTRL_CAP);

    const C1 = { x: c1.x + dC1.x, y: c1.y + dC1.y };
    const C2 = { x: c2.x + dC2.x, y: c2.y + dC2.y };

    const d = `M${p0.x},${p0.y} C${C1.x},${C1.y} ${C2.x},${C2.y} ${p3.x},${p3.y}`;
    basePathRef.current.setAttribute('d', d);
    elecPathRef.current.setAttribute('d', d);

    const pathLen = basePathRef.current.getTotalLength();

    // Electric animation
    const dt = 0.0016 * now;
    const dashOffset = -((now * 0.18) % 1) * 600 - 60 * Math.sin(dt);
    elecPathRef.current.style.strokeDashoffset = dashOffset.toString();

    // Sparks animation
    sparksRef.current.forEach(spark => {
      spark.pos = (spark.pos + spark.speed * 0.016) % 1;
      const p = basePathRef.current!.getPointAtLength(spark.pos * pathLen);
      spark.el.setAttribute('cx', p.x.toString());
      spark.el.setAttribute('cy', p.y.toString());

      const r = 1.8 + Math.abs(Math.sin(now * 0.008 + spark.pos * 18)) * 2.1;
      spark.el.setAttribute('r', r.toFixed(2));
      spark.el.style.opacity = (0.6 + Math.random() * 0.4).toFixed(2);
    });
  };

  const frame = (now: number) => {
    computePath(now);
    animationRef.current = requestAnimationFrame(frame);
  };

  const handleCardHover = (cardId: string, event: React.PointerEvent) => {
    const state = cardsState.current.get(cardId);
    if (!state || !wrapRef.current) return;

    const rect = wrapRef.current.getBoundingClientRect();
    state.hover = true;
    state.hx = event.clientX - rect.left;
    state.hy = event.clientY - rect.top;
  };

  const handleCardMove = (cardId: string, event: React.PointerEvent) => {
    const state = cardsState.current.get(cardId);
    if (!state || !state.hover || !wrapRef.current) return;

    const rect = wrapRef.current.getBoundingClientRect();
    state.hx = event.clientX - rect.left;
    state.hy = event.clientY - rect.top;
  };

  const handleCardLeave = (cardId: string) => {
    const state = cardsState.current.get(cardId);
    if (!state) return;
    state.hover = false;
  };

  useEffect(() => {
    // Initialize card states
    cards.forEach(card => {
      cardsState.current.set(card.id, {
        dx: 0, dy: 0, vx: 0, vy: 0,
        hover: false, hx: 0, hy: 0,
        element: null
      });
    });

    resize();
    createSparks();

    if (elecPathRef.current) {
      elecPathRef.current.style.strokeDasharray = randomDash(600);
    }

    animationRef.current = requestAnimationFrame(frame);

    const handleResize = () => resize();
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [cards]);

    useEffect(() => {
    // Connect card elements to state after render
    const cardElements = wrapRef.current?.querySelectorAll('.timeline-card');
    cardElements?.forEach((el, index) => {
      const card = cards[index];
      if (card) {
        const state = cardsState.current.get(card.id);
        if (state) {
          state.element = el;
        }
      }
    });

    // Set up intersection observer for reveal animation
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('in');
        }
      });
    }, { threshold: 0.2 });

    cardElements?.forEach(el => observer.observe(el));

    return () => {
      cardElements?.forEach(el => observer.unobserve(el));
    };
  });

  return (
    <div className={`electric-timeline ${className}`}>
      <div className="timeline-wrap" ref={wrapRef}>
        <svg ref={svgRef} viewBox="0 0 1000 400" preserveAspectRatio="none" aria-hidden="true">
          <defs>
            <linearGradient id="timeline-grad" x1="0" y1="0" x2="1" y2="0">
              <stop offset="0%" stopColor="var(--brand)" />
              <stop offset="100%" stopColor="var(--brand-2)" />
            </linearGradient>
          </defs>

          <path ref={refPathRef} id="ref-path" style={{ visibility: 'hidden' }} />
          <path ref={basePathRef} className="path-base" />
          <path ref={elecPathRef} className="elec-path" />
          <g ref={sparksGroupRef} id="sparks"></g>
        </svg>

        {cards.map((card) => (
          <article
            key={card.id}
            className="timeline-card reveal"
            onPointerEnter={(e) => handleCardHover(card.id, e)}
            onPointerMove={(e) => handleCardMove(card.id, e)}
            onPointerLeave={() => handleCardLeave(card.id)}
          >
            <h3>{card.title}</h3>
            {card.description && <p>{card.description}</p>}
          </article>
        ))}
      </div>
    </div>
  );
};

export default ElectricTimeline;
